/**
 * PhotoVogue moderation-queue filter definitions.
 *
 * Self-contained (a local `PhotoFilterDefinition` type) rather than reusing the
 * talent `NavigationFilterDefinition`, which is bound to `SubmissionFilters` and
 * assumes array-only (multiSelect/range/toggle) semantics. PhotoVogue needs
 * single-select facets (category, type) too, so we model our own.
 *
 * In Step 2 the option lists (categories, themes, tags) come from `photovogue-api`
 * dictionaries; here they are static sample values for the mock UI.
 */

import { PHOTO_STATES, PHOTO_STATE_LABELS } from './types';

export type PhotoFilterKind = 'multiSelect' | 'single';

export type PhotoFilterKey = 'states' | 'category' | 'type' | 'themes' | 'tags';

export interface PhotoFilterOption {
  value: string;
  label: string;
}

export interface PhotoFilterDefinition {
  key: PhotoFilterKey;
  label: string;
  kind: PhotoFilterKind;
  options: PhotoFilterOption[];
}

export const PHOTO_CATEGORIES: string[] = [
  'Portrait',
  'Fashion',
  'Still Life',
  'Landscape',
  'Documentary',
  'Street',
  'Conceptual',
  'Nude',
];

export const PHOTO_THEMES: string[] = [
  'Identity',
  'Nature',
  'Urban',
  'Family',
  'Travel',
  'Beauty',
  'Politics',
  'Memory',
];

export const PHOTO_TAGS: string[] = [
  'analog',
  'black-and-white',
  'studio',
  'natural-light',
  'portrait',
  'editorial',
  'reportage',
  'minimal',
];

const toOptions = (values: string[]): PhotoFilterOption[] =>
  values.map((value) => ({ value, label: value }));

export const PHOTO_FILTER_DEFINITIONS: PhotoFilterDefinition[] = [
  {
    key: 'states',
    label: 'State',
    kind: 'multiSelect',
    options: PHOTO_STATES.map((value) => ({
      value,
      label: PHOTO_STATE_LABELS[value],
    })),
  },
  {
    key: 'category',
    label: 'Category',
    kind: 'single',
    options: toOptions(PHOTO_CATEGORIES),
  },
  {
    key: 'type',
    label: 'Editorial type',
    kind: 'single',
    options: [
      { value: 'best', label: 'Best' },
      { value: 'daily', label: 'Pic of the Day' },
    ],
  },
  {
    key: 'themes',
    label: 'Themes',
    kind: 'multiSelect',
    options: toOptions(PHOTO_THEMES),
  },
  {
    key: 'tags',
    label: 'Tags',
    kind: 'multiSelect',
    options: toOptions(PHOTO_TAGS),
  },
];
